import { useState } from 'preact/hooks';
import type { MailItem } from '../../game/account';
import { ITEMS, RARITY_COLOR } from '../../game/items';
import { Window } from '../../ui/app/Window';
import {
  type ClaimResult,
  claimAll,
  claimMail,
  deleteMail,
  deleteRead,
  hasAttachments,
  markRead,
  unreadCount,
} from './mail';
import { Amount, Cell, Empty, TipLayer, commit, fmt, timeAgo, toast, useCtx, useNow } from './ui';

function haul(r: ClaimResult): string {
  const parts: string[] = [];
  if (r.silver) parts.push(`${fmt(r.silver)} silver`);
  if (r.gold) parts.push(`${fmt(r.gold)} gold`);
  const n = r.items.reduce((s, i) => s + i.count, 0);
  if (n) parts.push(`${fmt(n)} item${n > 1 ? 's' : ''}`);
  return parts.join(', ');
}

function Attachments({ m }: { m: MailItem }) {
  if (!hasAttachments(m)) return null;
  return (
    <div class="ml-attach">
      {m.silver > 0 && <Amount kind="silver" n={m.silver} />}
      {m.gold > 0 && <Amount kind="gold" n={m.gold} />}
      {m.items.map((it) => (
        <span key={it.id} class="ml-item">
          <Cell item={it.id} count={it.count} size={40} />
          <span style={{ color: RARITY_COLOR[ITEMS[it.id]?.rarity ?? 'normal'] }}>
            {ITEMS[it.id]?.name ?? it.id}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Panel `mail`: letters from the market broker, overflow and gifts. */
export function MailPanel() {
  const ctx = useCtx();
  const now = useNow(30_000);
  const [sel, setSel] = useState<string | null>(null);
  if (!ctx) return null;
  const { p, a } = ctx;
  const mails = a.mail;
  const cur = mails.find((m) => m.id === sel) ?? null;
  const unread = unreadCount(a);
  const claimable = mails.filter(hasAttachments).length;

  const claim = (id: string) => {
    const r = claimMail(p, id);
    if (!r) return;
    const got = haul(r);
    if (got) toast(`Claimed ${got}`, '#f1c33f');
    if (!r.complete) toast('Your bag is full — some items stayed in the letter.', '#e5484d');
    commit();
  };

  return (
    <Window title="Mailbox" width={800} height={560} className="fx-win fx-mail">
      <div class="fx-bar-top">
        <span class="fx-h">
          {mails.length} letter{mails.length === 1 ? '' : 's'} · {unread} unread
        </span>
        <span class="fx-spacer" />
        <button
          class="btn sm"
          disabled={!claimable}
          onClick={() => {
            const r = claimAll(p);
            const got = haul(r);
            if (got) toast(`Claimed ${got}`, '#f1c33f');
            if (!r.complete) toast('Your bag is full — some items stayed behind.', '#e5484d');
            commit();
          }}
        >
          Claim all
        </button>
        <button
          class="btn sm"
          disabled={!mails.some((m) => m.read && !hasAttachments(m))}
          onClick={() => {
            const n = deleteRead(a);
            if (sel && !a.mail.some((m) => m.id === sel)) setSel(null);
            toast(`Deleted ${n} letter${n === 1 ? '' : 's'}`);
            commit();
          }}
        >
          Delete read
        </button>
      </div>
      <div class="ml-body">
        <ul class="ml-list fx-scroll">
          {mails.length === 0 && (
            <Empty title="No mail">
              Market proceeds and anything that didn't fit in your bag arrive here.
            </Empty>
          )}
          {mails.map((m) => (
            <li key={m.id}>
              <button
                class={`ml-row${sel === m.id ? ' is-sel' : ''}${m.read ? '' : ' is-unread'}`}
                onClick={() => {
                  setSel(m.id);
                  if (!m.read) {
                    markRead(a, m.id);
                    commit();
                  }
                }}
              >
                <span class="ml-dot" aria-hidden="true" />
                <span class="ml-from">{m.from}</span>
                <span class="ml-when">{timeAgo(m.at, now)}</span>
                <span class="ml-subj">{m.subject}</span>
                <span class="ml-icons">
                  {m.gold > 0 && (
                    <i class="fx-coin gold" style={{ width: '10px', height: '10px' }} />
                  )}
                  {m.silver > 0 && (
                    <i class="fx-coin silver" style={{ width: '10px', height: '10px' }} />
                  )}
                  {m.items.length > 0 && <i class="ml-parcel" />}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <section class="ml-read">
          {cur ? (
            <>
              <div class="ml-read-h">
                <h3>{cur.subject}</h3>
                <div class="fx-sub">
                  From <b>{cur.from}</b> · {new Date(cur.at).toLocaleString()}
                </div>
              </div>
              <div class="ml-read-body fx-scroll">
                {cur.body && <p>{cur.body}</p>}
                <Attachments m={cur} />
              </div>
              <div class="ml-read-foot">
                {hasAttachments(cur) ? (
                  <button class="btn btn--gold" onClick={() => claim(cur.id)}>
                    Claim
                  </button>
                ) : (
                  <span class="fx-dim">Nothing attached.</span>
                )}
                <span class="fx-spacer" />
                <button
                  class="btn"
                  disabled={hasAttachments(cur)}
                  title={hasAttachments(cur) ? 'Claim the attachments first' : undefined}
                  onClick={() => {
                    if (deleteMail(a, cur.id)) {
                      setSel(null);
                      commit();
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </>
          ) : (
            <Empty title={mails.length ? 'Select a letter' : 'Your mailbox is empty'} />
          )}
        </section>
      </div>
      <TipLayer />
    </Window>
  );
}
